import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogoutButton } from '../auth/LogoutButton';
import { useResponsive, useResponsiveNavigation } from '../../hooks/useResponsive';
import { useI18n } from '../../contexts/I18nContext';

interface HeaderProps {
  onMenuClick: () => void;
}

// Page title mapping function
const getPageTitles = (t: (key: string) => string): Record<string, string> => ({
  '/dashboard': t('navigation.dashboard'),
  '/holidays': t('navigation.holidays'),
  '/users': t('navigation.users'),
  '/reports': t('navigation.reports'),
  '/settings': t('navigation.settings'),
});

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user } = useAuth();
  const location = useLocation();
  const { isMobile, isSmallMobile } = useResponsive();
  const { isNavigationOpen } = useResponsiveNavigation();
  const { t, isRTL } = useI18n();
  
  const pageTitles = getPageTitles(t);
  const currentPageTitle = pageTitles[location.pathname] || 'Holiday Dashboard';
  
  // Responsive title logic - show condensed title on small mobile, full title on larger screens
  const displayTitle = isSmallMobile ? 'Dashboard' : (isMobile ? 'Holiday Dashboard' : currentPageTitle);

  return (
    <header 
      className="w-full shadow-sm border-b"
      style={{
        backgroundColor: 'var(--color-surface)',
        borderColor: 'var(--color-border)'
      }}
      role="banner"
    >
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Left side - Mobile menu and page title */}
        <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
          {/* Mobile menu button */}
          <button
            type="button"
            className="header-menu-btn lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 transition-colors duration-200"
            onClick={onMenuClick}
            aria-label={isNavigationOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isNavigationOpen}
            aria-controls="mobile-navigation"
          >
            {/* Hamburger/Close icon with animation */}
            <svg 
              className="w-6 h-6 transition-transform duration-200" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {isNavigationOpen ? (
                // Close icon (X)
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                // Hamburger icon
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Page title */}
          <div className={`${isRTL ? 'mr-4 lg:mr-6' : 'ml-4 lg:ml-6'}`}>
            <h1 className={`header-title text-xl font-semibold text-gray-900 dark:text-white truncate`} style={{ textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
              {displayTitle}
            </h1>
          </div>
        </div>

        {/* Right side - User info and actions */}
        <div className={`header-actions flex items-center ${isRTL ? 'space-x-reverse space-x-6 sm:space-x-8 flex-row-reverse' : 'space-x-3 sm:space-x-4'}`} style={{ marginLeft: isRTL ? '0' : 'auto', marginRight: isRTL ? 'auto' : '0' }}>
          {/* Logout button - appears first in RTL */}
          {isRTL && <div className="mr-6"><LogoutButton /></div>}
          
          {/* User info - Desktop and Tablet */}
          <div className={`hidden md:flex md:items-center ${isRTL ? 'md:space-x-reverse md:space-x-4 mr-4' : 'md:space-x-3'}`}>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3 flex-row'}`}>
              <div 
                className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center ring-2 ring-blue-200 dark:ring-blue-800"
                role="img"
                aria-label={`${user?.username} avatar`}
              >
                <span className="text-sm font-medium text-blue-700 dark:text-blue-200">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="text-sm min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate" style={{ textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
                  {user?.username}
                </p>
                <p className="text-gray-500 dark:text-gray-400 capitalize truncate" style={{ textAlign: isRTL ? 'right' : 'left', fontFamily: isRTL ? 'Cairo, sans-serif' : 'inherit' }}>
                  {t(`auth.roles.${user?.role}`) || user?.role}
                </p>
              </div>
            </div>
          </div>

          {/* User info - Small Mobile and Tablet (avatar only) */}
          <div className="md:hidden">
            <div 
              className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center ring-2 ring-blue-200 dark:ring-blue-800"
              role="img"
              aria-label={`${user?.username} avatar`}
              title={`${user?.username} (${user?.role})`}
            >
              <span className="text-sm font-medium text-blue-700 dark:text-blue-200">
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
          </div>

          {/* Logout button - appears last in LTR */}
          {!isRTL && <LogoutButton />}
        </div>
      </div>
    </header>
  );
};